/**
 * Consulting & Strategy Discovery — the third Extern Labs vertical.
 *
 * Deliberately a SIBLING of `finance-discovery-events.ts` / `data-discovery-events.ts` rather than a
 * shared factory that builds names from a prefix: the event names must stay LITERAL so they are
 * greppable, land in Mixpanel's Lexicon, and survive the tracking-guard's `track(EVENTS.NAME)`
 * detection. Same event vocabulary as the other verticals (the engine emits the same milestones for
 * every vertical) — only the prefix differs, so all three can be read side by side on one board.
 */
export const CONSULTING_DISCOVERY_EVENTS = {
  ELIGIBILITY_RESOLVED: 'consulting_discovery_eligibility_resolved',
  // Fired on /payment for BOTH arms, BEFORE the paywall. The ITT denominator for the pre-paywall
  // conversion read — ELIGIBILITY_RESOLVED fires at checkout success, which is after this moment.
  PREPAYWALL_ELIGIBILITY_RESOLVED: 'consulting_discovery_prepaywall_eligibility_resolved',
  ENTERED: 'consulting_discovery_entered',
  // THE PRE-PAYWALL HAND-OFF, the conversion mechanic. An unpaid learner who finishes the sprint meets
  // a modal that names the artifact they just made and asks for the trial. HANDOFF_SHOWN is the
  // denominator for that ask; TRIAL_CLICKED over SHOWN is the conversion rate this experiment exists to
  // move. A subscribed learner never fires any of the three.
  HANDOFF_SHOWN: 'consulting_discovery_handoff_shown',
  HANDOFF_TRIAL_CLICKED: 'consulting_discovery_handoff_trial_clicked',
  HANDOFF_DISMISSED: 'consulting_discovery_handoff_dismissed',
  // Per-step progress — emitted the FIRST time each of the 18 steps is reached. Powers the
  // abandon-point funnel (the furthest stepIndex a user reaches = their completion %).
  STEP_VIEWED: 'consulting_discovery_step_viewed',
  MODULE_COMPLETED: 'consulting_discovery_module_completed',
  ARCHETYPE_REVEALED: 'consulting_discovery_archetype_revealed',
  FLOW_COMPLETED: 'consulting_discovery_flow_completed',
  RECOMMENDATION_CLICKED: 'consulting_discovery_recommendation_clicked',
  OFFER_SHOWN: 'consulting_discovery_offer_shown',
  OFFER_STARTED: 'consulting_discovery_offer_started',
  OFFER_DISMISSED: 'consulting_discovery_offer_dismissed',
  // THE IN-SPRINT TRIAL BAR — pinned to the bottom of every sprint screen for unpaid learners.
  // Asks for the trial DURING the work, where HANDOFF_* asks after it. Kept as its own pair so
  // the two moments can be compared instead of blended.
  TRIAL_BAR_SHOWN: 'consulting_discovery_trial_bar_shown',
  TRIAL_BAR_CLICKED: 'consulting_discovery_trial_bar_clicked',
} as const;

export type ConsultingDiscoveryEventName =
  (typeof CONSULTING_DISCOVERY_EVENTS)[keyof typeof CONSULTING_DISCOVERY_EVENTS];
